import { apiFetch, ApiError } from './client'
import { auditResponseSchema, type AuditResponse } from '@/lib/schemas/audit'
import { filenameFromDisposition, triggerBrowserDownload } from './download'

/**
 * Audit Log API (Screen 8). One filtered getter plus an xlsx downloader of the
 * current filter result. Blank filters are omitted so the server applies its
 * last-7-days default; pagination is limit/offset.
 */

function messageFromBody(body: unknown): string | undefined {
  if (!body || typeof body !== 'object') return undefined
  const detail = (body as { detail?: unknown }).detail
  return typeof detail === 'string' ? detail : undefined
}

async function fail(res: Response, fallback: string): Promise<never> {
  const body = await res.json().catch(() => null)
  const detail = messageFromBody(body)
  throw new ApiError(res.status, detail ?? `${fallback} (${res.status})`, detail)
}

export interface AuditFilters {
  start?: string
  end?: string
  event_type?: string
  user?: string
  order_number?: string
  q?: string
  limit?: number
  offset?: number
}

type QueryValue = string | number | undefined

/** Build a `?a=b` string, dropping blank/undefined values. */
function buildQuery(entries: Array<[string, QueryValue]>): string {
  const params = new URLSearchParams()
  for (const [key, value] of entries) {
    if (value === undefined || value === '') continue
    params.set(key, String(value))
  }
  const s = params.toString()
  return s ? `?${s}` : ''
}

function filterEntries(filters: AuditFilters): Array<[string, QueryValue]> {
  return [
    ['start', filters.start],
    ['end', filters.end],
    ['event_type', filters.event_type],
    ['user', filters.user],
    ['order_number', filters.order_number],
    ['q', filters.q],
    ['limit', filters.limit],
    ['offset', filters.offset],
  ]
}

/** GET /api/audit — filtered, paginated, newest-first. */
export async function getAudit(filters: AuditFilters): Promise<AuditResponse> {
  const path = `/api/audit${buildQuery(filterEntries(filters))}`
  const res = await apiFetch(path, { method: 'GET' })
  if (!res.ok) return fail(res, 'Failed to load audit log')
  return auditResponseSchema.parse(await res.json())
}

/** GET /api/audit?format=xlsx — download the current filter result as Excel. */
export async function downloadAuditXlsx(filters: AuditFilters): Promise<void> {
  const entries = filterEntries(filters).filter(([key]) => key !== 'limit' && key !== 'offset')
  entries.push(['format', 'xlsx'])
  const res = await apiFetch(`/api/audit${buildQuery(entries)}`, { method: 'GET' })
  if (!res.ok) return fail(res, 'Export failed')
  const blob = await res.blob()
  const filename =
    filenameFromDisposition(res.headers.get('content-disposition')) ?? 'audit-log.xlsx'
  triggerBrowserDownload(blob, filename)
}
